
import type { PropType, SetupContext } from 'vue'
import type { EpbSelectDropdownOptionType, EpbSelectOption } from './epb-select.types'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { ColorEnum } from '../../../enums'
import { UiPopover } from '../../keen-ui'
import { EpbText } from '../epb-text'
import { EpbVirtualScroller, EpbVirtualScrollerList } from '../epb-virtual-scroller'
import EpbSelectDropdownOption from './epb-select-dropdown-option.component.vue'

export default defineComponent({
  name: 'EpbSelectDropdown',
  components: {
    EpbSelectDropdownOption,
    EpbText,
    UiPopover,
    EpbVirtualScroller,
    EpbVirtualScrollerList,
  },
  props: {
    value: {
      type: [String, Array] as PropType<string | Array<string>>,
      required: true,
    },
    options: {
      type: Array as PropType<Array<EpbSelectOption>>,
      required: true,
    },
    search: {
      type: Boolean,
      default: false,
    },
    optionType: {
      type: String as PropType<EpbSelectDropdownOptionType>,
      default: 'default',
    },
    showSelectAll: {
      type: Boolean,
      default: undefined,
    },
    selectAllLabel: {
      type: String,
      default: undefined,
    },
    closeDisabled: {
      type: Boolean,
      default: false,
    },
    showChildrenOnClick: {
      type: Boolean,
      default: false,
    },
    shouldSearchChildren: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    input: (value: string | Array<string>) => {},
    change: (value: string | Array<string>) => {},
    open: () => {},
    close: () => {},
  },
  setup(props, ctx: SetupContext) {
    const baseClass = 'epb-select-dropdown'

    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const messages = $epb.messages.filters

    const popover = ref()
    const searchInput = ref<HTMLInputElement>()
    const optionsListRef = ref(null)
    const optionHeight = 36 // Default height for each option in pixels

    const query = ref('')

    const isMultiple = computed<boolean>(() => typeof props.value !== 'string')

    const isAnyOptionSelected = computed(() => props.value.length > 0)

    const areAllOptionsSelected = computed(() => props.value.length === props.options.length)

    const selectAllOption: EpbSelectOption = {
      value:
        (isMultiple.value ? messages.values.all.select : props.selectAllLabel) ??
        messages.values.all.default,
    }

    const selectAllOptionType = computed((): EpbSelectDropdownOptionType => {
      return isAnyOptionSelected.value && !areAllOptionsSelected.value
        ? 'checkbox-indeterminate'
        : props.optionType
    })

    const isShowSelectAllVisible = computed(() => {
      if (props.showSelectAll === undefined) {
        return isMultiple.value && props.optionType === 'default'
      }
      return props.showSelectAll
    })

    const isSelectAllOptionSelected = computed((): boolean => {
      return isMultiple.value && isAnyOptionSelected.value
    })

    function optionMatchesSearchText(option: EpbSelectOption, searchText: string): boolean {
      const normalizedSearch = searchText.toLowerCase()
      return (
        (option.label ?? option.value).toLowerCase().includes(normalizedSearch) ||
        option.value.toLowerCase().includes(normalizedSearch) ||
        !!option.help?.toLowerCase().includes(normalizedSearch)
      )
    }

    function optionMatchesSearchTextExactly(option: EpbSelectOption, searchText: string): boolean {
      const normalizedSearch = searchText.toLowerCase()
      return [option.label, option.value].some(field => field?.toLowerCase() === normalizedSearch)
    }

    function flattenAllOptions(
      options: Array<EpbSelectOption>,
      parentLevel = 0,
    ): Array<EpbSelectOption> {
      return options.flatMap(option => {
        const optionLevel = option.level ?? parentLevel

        return [
          { ...option, level: optionLevel },
          ...flattenAllOptions(option.children ?? [], optionLevel + 1),
        ]
      })
    }

    function flattenOptions(
      options: Array<EpbSelectOption>,
      parentLevel = 0,
    ): Array<EpbSelectOption> {
      return options.flatMap(option => {
        const optionLevel = option.level !== undefined ? option.level : parentLevel
        const flatOption: EpbSelectOption = {
          ...option,
          level: optionLevel,
        }

        const activeQuery = query.value.trim()
        const matchesQuery = !activeQuery || optionMatchesSearchText(option, activeQuery)

        if (option.children && option.children.length > 0) {
          const shouldShowChildren = !props.showChildrenOnClick || isOptionSelected(option)

          if (activeQuery && props.shouldSearchChildren) {
            const matchingChildren = flattenOptions(option.children, optionLevel + 1).filter(
              child => optionMatchesSearchText(child, activeQuery),
            )
            if (matchesQuery || matchingChildren.length > 0) {
              return [flatOption, ...matchingChildren]
            }
            return []
          }

          if (shouldShowChildren) {
            return [flatOption, ...flattenOptions(option.children, optionLevel + 1)]
          }
        }

        return matchesQuery ? [flatOption] : []
      })
    }

    const useValueOption = computed<EpbSelectOption | null>(() => {
      const normalizedQuery = query.value.trim()
      if (!props.allowCreate || !normalizedQuery) {
        return null
      }
      const hasExactMatch = flattenAllOptions(props.options).some(option =>
        optionMatchesSearchTextExactly(option, normalizedQuery),
      )
      if (hasExactMatch) {
        return null
      }
      return {
        value: normalizedQuery,
        label: messages.useValue.replace('{value}', normalizedQuery),
      }
    })

    const visibleOptions = computed<Array<EpbSelectOption>>(() => {
      const normalizedQuery = query.value.trim()
      let filteredOptions = flattenOptions(props.options)

      if (props.search && normalizedQuery) {
        filteredOptions = filteredOptions.filter(option =>
          optionMatchesSearchText(option, normalizedQuery),
        )
      }

      if (useValueOption.value) {
        filteredOptions = [...filteredOptions, useValueOption.value]
      }

      if (isShowSelectAllVisible.value) {
        return [{ ...selectAllOption, level: 0 }, ...filteredOptions]
      }

      return filteredOptions
    })

    function isSelectAllOption(option: EpbSelectOption): boolean {
      return option.value === selectAllOption.value
    }

    function getOptionType(option: EpbSelectOption): EpbSelectDropdownOptionType {
      return isSelectAllOption(option) ? selectAllOptionType.value : props.optionType
    }

    function getOptionSelected(option: EpbSelectOption): boolean {
      return isSelectAllOption(option) ? isSelectAllOptionSelected.value : isOptionSelected(option)
    }

    function getAllDescendants(option: EpbSelectOption): string[] {
      const descendants: string[] = []

      if (option.children && option.children.length > 0) {
        for (const child of option.children) {
          descendants.push(child.value)
          descendants.push(...getAllDescendants(child))
        }
      }

      return descendants
    }

    function isOptionSelected(option: EpbSelectOption): boolean {
      const arrayValue = Array.isArray(props.value) ? props.value : props.value ? [props.value] : []

      if (arrayValue.includes(option.value)) {
        return true
      }

      const descendants = getAllDescendants(option)
      return descendants.some(descendantValue => arrayValue.includes(descendantValue))
    }

    function showCheckbox(): boolean {
      return (
        isMultiple.value &&
        (props.optionType === 'default' || props.optionType === 'checkbox-indeterminate')
      )
    }

    function selectAll(): void {
      const values =
        isMultiple.value && props.value.length !== props.options.length
          ? props.options.map(option => option.value)
          : []

      ctx.emit('change', values)
      ctx.emit('input', values)
    }

    function onOptionSelect(option: EpbSelectOption): void {
      if (isSelectAllOption(option)) {
        selectAll()
        return
      }

      let newValue: string | Array<string>
      let hasChanged = false

      if (isMultiple.value) {
        newValue = [...(props.value as Array<string>)]
        if (isOptionSelected(option)) {
          newValue.splice((props.value as Array<string>).indexOf(option.value), 1)
        } else {
          newValue.push(option.value)
        }
        hasChanged = true
      } else {
        if (props.showChildrenOnClick && props.optionType === 'radio') {
          newValue = isOptionSelected(option) ? (findParentValue(option) ?? '') : option.value
          hasChanged = true
        } else if (props.optionType === 'radio' && isOptionSelected(option)) {
          newValue = ''
          hasChanged = true
        } else {
          newValue = option.value
          hasChanged = !isOptionSelected(option)
        }
      }

      ctx.emit('input', newValue)

      if (hasChanged) {
        ctx.emit('change', newValue)
      }
    }

    function onDropdownOptionClick(): void {
      if (
        !isMultiple.value &&
        !props.closeDisabled &&
        !(props.showChildrenOnClick && props.optionType === 'radio')
      ) {
        closePopover()
      } else if (props.showChildrenOnClick) {
        popover.value?.open()
      }
    }

    function closePopover(): void {
      if (popover.value) {
        popover.value.close()
      }
    }

    function findParentValue(option: EpbSelectOption): string | undefined {
      if (!option || isSelectAllOption(option) || (option.level ?? 0) === 0) return undefined

      const currentLevel = option.level ?? 0
      const currentIndex = visibleOptions.value.findIndex(opt => opt === option)

      if (currentIndex === -1) return undefined

      for (let i = currentIndex - 1; i >= 0; i--) {
        const potentialParent = visibleOptions.value[i]
        if (!potentialParent) continue

        const parentLevel = potentialParent.level ?? 0
        if (parentLevel === currentLevel - 1) {
          return potentialParent.value
        }
      }

      return undefined
    }

    watch(visibleOptions, () => {
      if (props.showChildrenOnClick) {
        // When showChildrenOnClick is true, the children options are not displayed
        // So the virtual scroller might not scroll to the correct position
        // because the options are rendered in different positions
        // We need to scroll to the selected option manually
        const index = visibleOptions.value.findIndex(opt => opt.value === props.value)
        const option = visibleOptions.value[index]

        if (optionsListRef.value) {
          optionsListRef.value.$el.scrollTo(
            0,
            index * optionHeight - optionHeight * (option.level ?? 0),
          )
        }
      }
    })

    return {
      baseClass,
      optionsListRef,
      optionHeight,
      messages,
      onOptionSelect,
      onDropdownOptionClick,
      popover,
      query,
      searchInput,
      isMultiple,
      selectAllOption,
      isSelectAllOptionSelected,
      areAllOptionsSelected,
      selectAllOptionType,
      selectAll,
      showCheckbox,
      isShowSelectAllVisible,
      visibleOptions,
      ColorEnum,
      findParentValue,
      getOptionType,
      getOptionSelected,
    }
  },
})
